import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Loader2, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  service: z.string().min(1, 'Please select a service'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      service: '',
      message: '',
    },
  });

  const selectedService = watch('service');

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, data);
      
      if (response.status === 200 || response.status === 201) {
        toast.success('Message sent successfully!', {
          description: 'We\'ll get back to you as soon as possible.',
        });
        reset();
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or call us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10">
      <div className="mb-6">
        <h3 className="text-3xl font-bold text-slate-900 mb-2">Send Us a Message</h3>
        <p className="text-slate-600">Fill out the form below and we'll respond within 24 hours</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Name Field */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-slate-700 font-medium">
            Full Name *
          </Label>
          <Input
            id="name"
            {...register('name')}
            placeholder="John Doe"
            className={`h-12 ${errors.name ? 'border-red-500' : ''}`}
          />
          {errors.name && (
            <p className="text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-700 font-medium">
            Email Address *
          </Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            placeholder="john@example.com"
            className={`h-12 ${errors.email ? 'border-red-500' : ''}`}
          />
          {errors.email && (
            <p className="text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Phone Field */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-slate-700 font-medium">
            Phone Number *
          </Label>
          <Input
            id="phone"
            type="tel"
            {...register('phone')}
            placeholder="604-123-4567"
            className={`h-12 ${errors.phone ? 'border-red-500' : ''}`}
          />
          {errors.phone && (
            <p className="text-sm text-red-600">{errors.phone.message}</p>
          )}
        </div>

        {/* Service Selection */}
        <div className="space-y-2">
          <Label htmlFor="service" className="text-slate-700 font-medium">
            Service Needed *
          </Label>
          <Select
            value={selectedService}
            onValueChange={(value) => setValue('service', value)}
          >
            <SelectTrigger className={`h-12 ${errors.service ? 'border-red-500' : ''}`}>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="collision-repair">Collision Repair</SelectItem>
              <SelectItem value="auto-painting">Auto Painting</SelectItem>
              <SelectItem value="dent-removal">Dent Removal</SelectItem>
              <SelectItem value="general-inquiry">General Inquiry</SelectItem>
            </SelectContent>
          </Select>
          {errors.service && (
            <p className="text-sm text-red-600">{errors.service.message}</p>
          )}
        </div>

        {/* Message Field */}
        <div className="space-y-2">
          <Label htmlFor="message" className="text-slate-700 font-medium">
            Message *
          </Label>
          <Textarea
            id="message"
            {...register('message')}
            placeholder="Tell us about your vehicle and what service you need..."
            rows={5}
            className={`resize-none ${errors.message ? 'border-red-500' : ''}`}
          />
          {errors.message && (
            <p className="text-sm text-red-600">{errors.message.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="mr-2 h-5 w-5" />
              Send Message
            </>
          )}
        </Button>

        <p className="text-sm text-slate-500 text-center">
          * Required fields
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
